import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChevronRight,
  MapPin,
  BadgeEuro,
  ShieldCheck,
  Wrench,
  CheckCircle2,
  ThermometerSun,
} from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { Seo, SITE_URL } from "@/components/Seo";
import { ExitIntentPopup } from "@/components/ExitIntentPopup";
import { getCity } from "@/data/cities";

const ENGINEER_IMG = "https://images.pexels.com/photos/6471913/pexels-photo-6471913.jpeg";

const usps = [
  { icon: BadgeEuro, text: "Bis zu 70% staatliche Förderung – wir übernehmen den Antrag" },
  { icon: ShieldCheck, text: "Festpreisgarantie ohne versteckte Kosten" },
  { icon: Wrench, text: "Zertifizierte Fachpartner aus Ihrer Region" },
];

export default function CityLanding() {
  const { city: slug } = useParams();
  const city = getCity(slug);
  if (!city) return <Navigate to="/" replace />;

  const title = `Wärmepumpe ${city.name} – Installation mit bis zu 70% Förderung | Wärmekraft`;
  const description = `Wärmepumpe in ${city.name} installieren lassen ✓ bis zu 70% Förderung ✓ Festpreisgarantie ✓ lokale Fachpartner. Kostenloses Angebot in 2 Minuten anfordern.`;

  const faqs = [
    {
      q: `Was kostet eine Wärmepumpe inkl. Installation in ${city.name}?`,
      a: `Die Kosten für eine Wärmepumpe in ${city.name} liegen je nach Gebäude und Typ meist zwischen 15.000 € und 35.000 €. Dank bis zu 70% Förderung reduziert sich Ihr Eigenanteil erheblich. Ihr genaues Festpreis-Angebot erhalten Sie kostenlos über unser Formular.`,
    },
    {
      q: `Welche Förderung gibt es für Wärmepumpen in ${city.name}?`,
      a: `In ${city.name} (${city.region}) profitieren Sie von der bundesweiten BEG-Förderung: Grundförderung, Klimageschwindigkeits- und Einkommensbonus lassen sich auf bis zu 70% kombinieren. Wir prüfen Ihren individuellen Anspruch und übernehmen die komplette Antragstellung.`,
    },
    {
      q: `Eignet sich mein Altbau in ${city.name} für eine Wärmepumpe?`,
      a: `Ja. Moderne Wärmepumpen arbeiten auch in vielen Altbauten effizient – oft schon mit vorhandenen Heizkörpern. Unsere Fachpartner prüfen Ihre Bausubstanz vor Ort und empfehlen die passende Lösung.`,
    },
    {
      q: `Wie schnell kann installiert werden?`,
      a: `Nach Ihrer Anfrage meldet sich ein Fachpartner aus ${city.name} innerhalb von 24 Stunden. Die eigentliche Installation dauert in der Regel nur 1–3 Tage.`,
    },
  ];

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Wärmepumpen-Installation",
      areaServed: { "@type": "City", name: city.name },
      provider: { "@type": "Organization", name: "Wärmekraft", url: SITE_URL },
      name: `Wärmepumpe Installation ${city.name}`,
      description,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <div className="bg-bone min-h-screen overflow-x-hidden">
      <Seo title={title} description={description} path={`/waermepumpe/${city.slug}`} jsonLd={jsonLd} />
      <ExitIntentPopup />

      <header className="absolute top-0 left-0 right-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="city-logo">
            <img src="/waermekraft-logo.png" alt="Wärmekraft" className="h-9 w-auto" />
          </Link>
          <Link
            to="/funnel"
            data-testid="city-header-cta"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-forest px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand transition-colors"
          >
            Angebot einholen <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </header>

      {/* HERO */}
      <section className="relative pt-32 pb-20 lg:pt-40">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 rounded-full bg-brand/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-brand">
              <MapPin className="h-3.5 w-3.5" /> {city.name} · {city.region}
            </span>
            <h1 className="mt-6 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-forest">
              Wärmepumpe in <span className="text-terracotta">{city.name}</span>
            </h1>
            <p className="mt-6 text-lg text-[#5A6172] max-w-md leading-relaxed">{city.intro}</p>
            <Link
              to="/funnel"
              data-testid="city-hero-cta"
              className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-terracotta px-8 py-4 text-base font-bold text-white shadow-lg shadow-terracotta/30 hover:bg-terracotta-hover hover:-translate-y-0.5 transition-all"
            >
              Kostenloses Angebot für {city.name} <ChevronRight className="h-5 w-5" />
            </Link>
            <div className="mt-8 space-y-3">
              {usps.map((u, i) => (
                <div key={i} className="flex items-center gap-3 text-sm font-semibold text-forest">
                  <u.icon className="h-5 w-5 text-brand shrink-0" /> {u.text}
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
            className="rounded-2xl overflow-hidden shadow-2xl shadow-forest/20 aspect-[4/5]"
          >
            <img src={ENGINEER_IMG} alt={`Wärmepumpen-Installation in ${city.name}`} className="w-full h-full object-cover" />
          </motion.div>
        </div>
      </section>

      {/* DISTRICTS */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-display font-bold text-2xl lg:text-3xl text-forest tracking-tight">
            Wärmepumpen-Service in ganz {city.name}
          </h2>
          <p className="mt-3 text-[#5A6172] max-w-2xl">
            Wir sind in allen Stadtteilen für Sie da – Postleitzahlen {city.plz}:
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            {city.districts.map((d) => (
              <span key={d} className="inline-flex items-center gap-2 rounded-full border border-[#D9DBE0] bg-bone px-4 py-2 text-sm font-semibold text-forest">
                <MapPin className="h-4 w-4 text-terracotta" /> {d}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-6">
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-terracotta">Häufige Fragen</span>
          <h2 className="mt-3 font-display font-bold text-3xl text-forest tracking-tight">
            Wärmepumpe {city.name} – Ihre Fragen
          </h2>
          <Accordion type="single" collapsible className="mt-8">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-[#D9DBE0]">
                <AccordionTrigger className="text-base font-bold text-forest hover:no-underline py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#5A6172] leading-relaxed text-[15px]">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-forest">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <CheckCircle2 className="h-10 w-10 text-pink mx-auto" />
          <h2 className="mt-5 font-display font-black text-3xl lg:text-4xl text-white tracking-tight">
            Jetzt Wärmepumpe in {city.name} anfragen
          </h2>
          <p className="mt-4 text-white/70">Kostenlos, unverbindlich und in nur 2 Minuten.</p>
          <Link
            to="/funnel"
            data-testid="city-final-cta"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-pink px-9 py-4 text-base font-bold text-white hover:bg-pink-hover hover:-translate-y-0.5 transition-all"
          >
            Angebot einholen <ChevronRight className="h-5 w-5" />
          </Link>
        </div>
      </section>

      <footer className="bg-forest border-t border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <ThermometerSun className="h-5 w-5 text-pink" />
            <span className="font-display font-extrabold text-white">Wärme<span className="text-pink">kraft</span></span>
          </div>
          <div className="flex gap-4 text-sm text-white/50">
            <Link to="/waermepumpe/wuppertal" className="hover:text-white transition-colors">Wuppertal</Link>
            <Link to="/waermepumpe/duesseldorf" className="hover:text-white transition-colors">Düsseldorf</Link>
            <Link to="/waermepumpe/duisburg" className="hover:text-white transition-colors">Duisburg</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
